import type { Session } from './commons';
import { GroupName, SessionEventName, StandaloneEventName } from './event-names';
import type { GroupEventName, GroupEventPayload, StandaloneEventPayload } from './events';

export enum PaymentGateway {
  Mollie = 'mollie',
  Klarna = 'klarna',
}

export interface StandaloneEvent<Name extends StandaloneEventName = StandaloneEventName> {
  name: Name;
  properties?: StandaloneEventPayload<Name>;
}

export interface GroupEvent<
  Group extends GroupName = GroupName,
  Name extends GroupEventName<Group> = GroupEventName<Group>,
> {
  group: Group;
  name: Name;
  data?: GroupEventPayload<Group, Name>;
  nonInteractive?: boolean;
}

export type SessionTrackingEvent = {
  name: SessionEventName;
} & Partial<Session>;

export type TrackingEvent = StandaloneEvent | GroupTrackingEvent | SessionTrackingEvent;

export type StandaloneTrackingEvent<Name extends StandaloneEventName = StandaloneEventName> =
  StandaloneEvent<Name>;

export type GroupTrackingEvent<
  Group extends GroupName = GroupName,
  Name extends GroupEventName<Group> = GroupEventName<Group>,
> = GroupEvent<Group, Name>;

export const isGroupTrackingEvent = (event: TrackingEvent): event is GroupTrackingEvent =>
  typeof event === 'object' && event !== null && 'group' in event;

export const isSessionTrackingEvent = (event: TrackingEvent): event is SessionTrackingEvent =>
  event.name === SessionEventName.SetSession;

export interface NormalizedStandaloneEvent<Name extends StandaloneEventName = StandaloneEventName> {
  event: Name;
  eventSource: 'petsdeli';
  eventData?: StandaloneEventPayload<Name>;
}

export interface NormalizedGroupEvent<
  Group extends GroupName = GroupName,
  Name extends GroupEventName<Group> = GroupEventName<Group>,
> {
  event: `${Group} ${Name}`;
  eventSource: 'petsdeli';
  eventName: Name;
  eventGroup: Group;
  eventData?: GroupEventPayload<Group, Name>;
  eventNonInteractive?: boolean;
}

export type NormalizedSessionEvent = {
  event: SessionEventName;
  eventSource: 'petsdeli';
} & Partial<Session>;

export type NormalizedEvent =
  NormalizedStandaloneEvent | NormalizedGroupEvent | NormalizedSessionEvent;
